/**
 * Panel de administración.
 *
 * Sigue la estructura del panel de PAMESA_ImageSimilarityEngine —chips de rango,
 * tarjetas de métrica, tabla de usuarios y cajón de detalle— con las métricas que
 * tienen sentido en un RAG. Dos diferencias deliberadas respecto a aquel:
 *
 * El tiempo de respuesta se muestra partido en recuperación y generación, porque
 * este backend las mide por separado (`diagnostics.elapsed_ms` frente a
 * `latency_ms`). Un único número medio no dice cuál de las dos hay que optimizar.
 *
 * Y hay una tarjeta para las respuestas sin fuentes. Es el fallo que destruye la
 * confianza en un asistente documental, ya se marca en ámbar en cada respuesta, y
 * aquí se convierte en tendencia en lugar de en un aviso que se ve una vez.
 *
 * Todo lo nominal —la tabla de usuarios y el registro de preguntas— sale de
 * llamadas distintas a las agregadas, y el backend registra cada lectura. Los
 * agregados se ven sin abrir a nadie.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useMatch, useNavigate } from "react-router-dom";

import { ApiError, api, type RangeDays } from "../api/client";
import type {
  ActorDetail,
  ActorRow,
  DocumentUsage,
  QuestionRow,
  UsageSummary,
} from "../api/types";
import "./admin.css";

const RANGES: Array<{ days: RangeDays; label: string }> = [
  { days: 7, label: "7 días" },
  { days: 30, label: "30 días" },
  { days: 90, label: "90 días" },
  { days: 365, label: "1 año" },
];

const STATUS_LABEL: Record<string, string> = {
  pending: "En cola",
  extracting: "Leyendo",
  chunking: "Troceando",
  embedding: "Indexando",
  indexed: "Indexados",
  failed: "Fallidos",
};

export function Admin() {
  const [days, setDays] = useState<RangeDays>(30);
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [docs, setDocs] = useState<DocumentUsage | null>(null);
  const [users, setUsers] = useState<ActorRow[] | null>(null);
  const [questions, setQuestions] = useState<QuestionRow[] | null>(null);
  const [includeHidden, setIncludeHidden] = useState(false);
  const [open, setOpen] = useState<ActorDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    // Cuatro llamadas en paralelo: son consultas independientes y encadenarlas
    // sumaría sus latencias sin ninguna ventaja.
    const [s, d, u, q] = await Promise.all([
      api.adminSummary(days),
      api.adminDocuments(days),
      api.adminUsers(days, includeHidden),
      api.adminQuestions(days),
    ]);
    setSummary(s);
    setDocs(d);
    setUsers(u.users);
    setQuestions(q.questions);
  }, [days, includeHidden]);

  useEffect(() => {
    setError(null);
    load().catch((e: unknown) => setError(describe(e)));
  }, [load]);

  async function toggleHidden(row: ActorRow) {
    try {
      await api.adminSetHidden(row.user_id, !row.hidden);
      await load();
    } catch (e: unknown) {
      setError(describe(e));
    }
  }

  const unsourcedShare =
    summary && summary.answers > 0
      ? (summary.unsourced_answers / summary.answers) * 100
      : 0;

  return (
    <div className="adm">
      <header className="page-head">
        <h1 className="page-title">Monitorización</h1>
        <AdminTabs />
        <div className="adm-ranges" role="group" aria-label="Rango temporal">
          {RANGES.map((range) => (
            <button
              key={range.days}
              type="button"
              className={`adm-chip${days === range.days ? " is-active" : ""}`}
              aria-pressed={days === range.days}
              onClick={() => setDays(range.days)}
            >
              {range.label}
            </button>
          ))}
        </div>
      </header>

      <div className="page-body adm-body">
        {error && <p className="adm-error">{error}</p>}

        <section className="adm-metrics" aria-label="Resumen de uso">
          <Metric
            value={summary ? summary.answers.toLocaleString("es-ES") : "—"}
            label="Preguntas respondidas"
            hint={
              summary ? `${summary.conversations} conversaciones` : undefined
            }
          />
          <Metric
            value={summary ? String(summary.active_users) : "—"}
            label="Usuarios activos"
            hint="Personas que han preguntado en el periodo"
          />
          <Metric
            value={
              summary
                ? formatSeconds(
                    summary.avg_retrieval_ms + summary.avg_generation_ms,
                  )
                : "—"
            }
            label="Tiempo medio de respuesta"
            hint={
              summary
                ? `${formatSeconds(summary.avg_retrieval_ms)} buscando · ${formatSeconds(summary.avg_generation_ms)} redactando`
                : undefined
            }
          />
          <Metric
            value={summary ? `${unsourcedShare.toFixed(1)} %` : "—"}
            label="Respuestas sin fuentes"
            hint={
              summary
                ? `${summary.unsourced_answers} de ${summary.answers}`
                : undefined
            }
            tone={unsourcedShare > 10 ? "warn" : undefined}
          />
        </section>

        <section className="card adm-trend" aria-label="Preguntas por día">
          <div className="adm-card-head">
            <h2 className="adm-card-title">Preguntas por día</h2>
            {summary && (
              <span className="adm-badge">
                {summary.prompt_tokens.toLocaleString("es-ES")} tokens de
                entrada · {summary.completion_tokens.toLocaleString("es-ES")} de
                salida
              </span>
            )}
          </div>
          {summary ? <Trend points={summary.by_day} days={days} /> : <Empty />}
        </section>

        <div className="adm-split">
          <section className="card" aria-label="Documentos">
            <div className="adm-card-head">
              <h2 className="adm-card-title">Documentos</h2>
              {docs && docs.never_cited > 0 && (
                <span className="adm-badge adm-badge--warn">
                  {docs.never_cited} sin citar nunca
                </span>
              )}
            </div>
            {docs ? (
              <>
                <div className="adm-tiles">
                  {Object.entries(docs.by_status).map(([status, count]) => (
                    <div key={status} className="adm-tile">
                      <span className="adm-tile-value">{count}</span>
                      <span className="adm-tile-label">
                        {STATUS_LABEL[status] ?? status}
                      </span>
                    </div>
                  ))}
                </div>
                <h3 className="adm-sub">Más citados</h3>
                <MostCited rows={docs.most_cited} />
              </>
            ) : (
              <Empty />
            )}
          </section>

          <section className="card adm-log" aria-label="Últimas preguntas">
            <div className="adm-card-head">
              <h2 className="adm-card-title">Últimas preguntas</h2>
              {questions && (
                <span className="adm-badge">{questions.length}</span>
              )}
            </div>
            {questions ? (
              questions.length === 0 ? (
                <p className="adm-empty">
                  Nadie ha preguntado nada en el periodo.
                </p>
              ) : (
                <ul className="adm-questions">
                  {questions.slice(0, 25).map((row) => (
                    <li key={row.message_id} className="adm-question">
                      <span className="adm-question-text">{row.question}</span>
                      <span className="adm-question-meta">
                        {formatWhen(row.asked_at)}
                        {row.latency_ms !== null &&
                          ` · ${formatSeconds(row.latency_ms)}`}
                        {row.chunks_used === 0 && (
                          <span className="adm-flag"> sin fuentes</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )
            ) : (
              <Empty />
            )}
          </section>
        </div>

        <section className="card" aria-label="Usuarios">
          <div className="adm-card-head">
            <h2 className="adm-card-title">Usuarios</h2>
            {users && <span className="adm-badge">{users.length}</span>}
            <label className="adm-toggle">
              <input
                type="checkbox"
                checked={includeHidden}
                onChange={(event) => setIncludeHidden(event.target.checked)}
              />
              <span>Ver ocultos</span>
            </label>
          </div>
          {users ? (
            users.length === 0 ? (
              <p className="adm-empty">
                Todavía nadie ha usado el asistente. Los usuarios aparecen aquí
                en cuanto hacen su primera pregunta.
              </p>
            ) : (
              <div className="adm-table-scroll">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th scope="col">Usuario</th>
                      <th scope="col">Rol</th>
                      <th scope="col" className="adm-num">
                        Preguntas
                      </th>
                      <th scope="col" className="adm-num">
                        Tiempo medio
                      </th>
                      <th scope="col">Última actividad</th>
                      {/* aria-label en la celda, no un <span> oculto: un elemento
                          absolutamente posicionado sin ancestro posicionado se
                          ancla al bloque contenedor inicial, se escapa del
                          contenedor con scroll y añadía 63 px de scroll a la
                          página entera. */}
                      <th scope="col" aria-label="Acciones" />
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((row) => (
                      <tr
                        key={row.user_id}
                        className={row.hidden ? "is-hidden" : undefined}
                      >
                        <th scope="row" className="adm-user">
                          <button
                            type="button"
                            className="adm-link"
                            onClick={() => {
                              api
                                .adminUser(row.user_id, days)
                                .then(setOpen)
                                .catch((e: unknown) => setError(describe(e)));
                            }}
                          >
                            {row.display_name ?? "Sin nombre"}
                          </button>
                          {row.email && <small>{row.email}</small>}
                        </th>
                        <td>{roleLabel(row.roles)}</td>
                        <td className="adm-num">{row.answers}</td>
                        <td className="adm-num">
                          {row.answers > 0
                            ? formatSeconds(row.avg_generation_ms)
                            : "—"}
                        </td>
                        <td>{formatWhen(row.last_seen_at)}</td>
                        <td className="adm-num">
                          <button
                            type="button"
                            className="btn btn--quiet"
                            onClick={() => void toggleHidden(row)}
                            title={
                              row.hidden
                                ? "Volver a mostrar en la vista por defecto"
                                : "Ocultar de la vista por defecto sin borrar su historial"
                            }
                          >
                            {row.hidden ? "Mostrar" : "Ocultar"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            <Empty />
          )}
        </section>
      </div>

      {open && <ActorDrawer detail={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

/** Barras relativas al documento más citado, que por el orden es el primero. */
function MostCited({
  rows,
}: {
  rows: Array<{ document_id: string; filename: string; citations: number }>;
}) {
  const peak = rows[0]?.citations ?? 0;
  if (peak === 0) {
    return <p className="adm-empty">Ninguna cita en el periodo.</p>;
  }
  return (
    <ul className="adm-bars">
      {rows.map((row) => (
        <li key={row.document_id} className="adm-bar-row">
          <span className="adm-bar-name" title={row.filename}>
            {row.filename}
          </span>
          <span className="adm-bar-track">
            <span
              className="adm-bar-fill"
              style={{ width: `${(row.citations / peak) * 100}%` }}
            />
          </span>
          <span className="adm-bar-value">{row.citations}</span>
        </li>
      ))}
    </ul>
  );
}

function AdminTabs() {
  const navigate = useNavigate();
  const onGuardrails = useMatch("/administracion/guardarrailes") !== null;
  return (
    <nav className="adm-tabs" aria-label="Secciones del panel">
      <button
        type="button"
        className={`adm-tab${onGuardrails ? "" : " is-active"}`}
        onClick={() => navigate("/administracion")}
      >
        Monitorización
      </button>
      <button
        type="button"
        className={`adm-tab${onGuardrails ? " is-active" : ""}`}
        onClick={() => navigate("/administracion/guardarrailes")}
      >
        Guardarraíles
      </button>
    </nav>
  );
}

function Metric({
  value,
  label,
  hint,
  tone,
}: {
  value: string;
  label: string;
  hint?: string;
  tone?: "warn";
}) {
  return (
    <div className={`card adm-metric${tone ? ` adm-metric--${tone}` : ""}`}>
      <span className="adm-metric-value">{value}</span>
      <span className="adm-metric-label">{label}</span>
      {hint && <span className="adm-metric-hint">{hint}</span>}
    </div>
  );
}

/**
 * Serie diaria como área SVG.
 *
 * Los días sin tráfico llegan ausentes del backend, no a cero: solo el cliente
 * sabe qué ventana pidió, así que rellenar los huecos es su trabajo. Sin ese
 * relleno una semana sin uso se dibujaría como una línea recta entre dos picos.
 */
function Trend({
  points,
  days,
}: {
  points: Array<{ day: string; answers: number }>;
  days: number;
}) {
  const byDay = new Map(points.map((p) => [p.day, p.answers]));
  const today = new Date();
  const series: Array<{ day: string; answers: number }> = [];
  for (let offset = days - 1; offset >= 0; offset -= 1) {
    const date = new Date(today);
    date.setDate(date.getDate() - offset);
    const key = date.toISOString().slice(0, 10);
    series.push({ day: key, answers: byDay.get(key) ?? 0 });
  }

  const peak = Math.max(1, ...series.map((p) => p.answers));
  const width = 100;
  const height = 32;
  const step = series.length > 1 ? width / (series.length - 1) : width;
  const line = series
    .map(
      (p, i) =>
        `${(i * step).toFixed(2)},${(height - (p.answers / peak) * height).toFixed(2)}`,
    )
    .join(" ");

  return (
    <div className="adm-trend-body">
      <svg
        className="adm-spark"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`Máximo ${peak} preguntas en un día`}
      >
        <polygon
          points={`0,${height} ${line} ${width},${height}`}
          className="adm-spark-area"
        />
        <polyline points={line} className="adm-spark-line" />
      </svg>
      <div className="adm-trend-scale">
        <span>{series[0]?.day}</span>
        <span>pico {peak}/día</span>
        <span>hoy</span>
      </div>
    </div>
  );
}

function ActorDrawer({
  detail,
  onClose,
}: {
  detail: ActorDetail;
  onClose: () => void;
}) {
  // A native `<dialog>` opened with `showModal()`, not an `<aside>` with a
  // hand-rolled backdrop. The previous markup was not a dialog at all: nothing
  // moved focus into it, Escape did nothing, Tab walked straight out of it into the
  // table behind, and on close focus was wherever it had been left. The platform
  // gives modality, Escape, focus containment and the top layer for free, and none
  // of them can drift out of sync with the CSS.
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const dialog = ref.current;
    opener.current = document.activeElement as HTMLElement | null;
    if (dialog !== null && !dialog.open) dialog.showModal();

    // Focus restore is the one thing the platform does *not* give us here, and it
    // is easy to assume it does. The browser restores focus when a dialog closes
    // while still in the document; this one is unmounted by React when the parent
    // clears its state, so the element goes away with the dialog still open and
    // focus falls to `<body>`. Measured: after closing the drawer, focus was on
    // BODY, so the next Tab restarted from the top of the page instead of from the
    // row the drawer was opened from.
    return () => {
      opener.current?.focus?.();
    };
  }, []);

  return (
    <dialog
      ref={ref}
      className="adm-drawer"
      aria-labelledby="adm-drawer-title"
      // Fires for Escape as well as for a programmatic close, so this is the one
      // place the parent's state has to be cleared.
      onCancel={onClose}
      onClose={onClose}
      // The backdrop is `::backdrop`, which is not a child, so a click outside is
      // detected by the click landing on the dialog's own box rather than on any
      // of its content.
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
    >
      <div className="adm-drawer-body">
        <header className="adm-drawer-head">
          <div>
            <strong id="adm-drawer-title">{detail.display_name ?? "Sin nombre"}</strong>
            {detail.email && <small>{detail.email}</small>}
            <small className="adm-drawer-role">{roleLabel(detail.roles)}</small>
          </div>
          <button type="button" className="btn btn--quiet" onClick={onClose}>
            Cerrar
          </button>
        </header>

        <div className="adm-drawer-stats">
          <div>
            <span className="adm-metric-value">{detail.answers}</span>
            <span className="adm-metric-label">
              Preguntas · {detail.days} días
            </span>
          </div>
          <div>
            <span className="adm-metric-value">
              {detail.answers > 0
                ? formatSeconds(detail.avg_generation_ms)
                : "—"}
            </span>
            <span className="adm-metric-label">Tiempo medio</span>
          </div>
          <div>
            <span className="adm-metric-value">
              {formatWhen(detail.first_seen_at)}
            </span>
            <span className="adm-metric-label">Primera vez</span>
          </div>
        </div>

        <h3 className="adm-sub">Preguntas recientes</h3>
        {detail.questions.length === 0 ? (
          <p className="adm-empty">Sin preguntas en el periodo.</p>
        ) : (
          <ul className="adm-questions">
            {detail.questions.map((row) => (
              <li key={row.message_id} className="adm-question">
                <span className="adm-question-text">{row.question}</span>
                <span className="adm-question-meta">
                  {formatWhen(row.asked_at)}
                  {row.chunks_used === 0 && (
                    <span className="adm-flag"> sin fuentes</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </dialog>
  );
}

function Empty() {
  return <p className="adm-empty">Cargando…</p>;
}

function roleLabel(roles: string[]): string {
  const names: string[] = [];
  if (roles.includes("rag.admin")) names.push("Admin");
  if (roles.includes("rag.librarian")) names.push("Documentalista");
  if (roles.includes("rag.chat")) names.push("Usuario");
  return names.join(" · ") || "Sin rol";
}

function formatSeconds(ms: number): string {
  if (!ms) return "—";
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms)} ms`;
}

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (sameDay) {
    return date.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
}

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "No se han podido cargar las métricas.";
}
